import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Sparkles } from 'lucide-react';
import BeetleEarthScene, { supportsWebGL2 } from '../three/BeetleEarthScene';
import AdventureGrid from './AdventureGrid';
import ThemeToggle from './ThemeToggle';
import BrandMark from './BrandMark';
import BeetleAdventureModal from './BeetleAdventureModal';

const AlmanacHome = () => {
  const adventuresRef = useRef(null);
  const [showAdventure, setShowAdventure] = useState(false);

  const goToAdventures = () => {
    adventuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-vent-bg" data-testid="almanac-home">
      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <BrandMark onOpenStory={() => setShowAdventure(true)} />
          <div className="flex items-center gap-3">
            <button
              onClick={goToAdventures}
              className="hidden sm:inline-flex text-sm text-zinc-300 hover:text-white transition-colors"
              data-testid="nav-adventures-btn"
            >
              Adventures
            </button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative h-[100svh] w-full overflow-hidden" data-testid="almanac-hero">
        {/* WebGL scene or static fallback */}
        <div className="absolute inset-0">
          {supportsWebGL2 ? (
            <BeetleEarthScene onEnter={goToAdventures} />
          ) : (
            <img
              src="/almanac/hero-fallback.png"
              alt="A beetle rolling the Earth through the cosmos"
              className="w-full h-full object-cover cursor-pointer"
              onClick={goToAdventures}
              data-testid="hero-fallback-img"
            />
          )}
        </div>

        {/* Readability gradients */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/80" />

        {/* Hero copy */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-5 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <h1 className="hero-white font-adventure font-extrabold leading-[1.08] text-4xl sm:text-5xl lg:text-6xl max-w-4xl mx-auto drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              STEP INTO AN ALMANAC OF AGENTIC ADVENTURES
            </h1>
            <p className="mt-6 text-sm sm:text-base text-white/75 tracking-[0.35em] uppercase">
              Weird. Wonderful. Wonked.
            </p>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            onClick={goToAdventures}
            className="pointer-events-auto mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-semibold text-sm tracking-wide hover:scale-105 transition-transform"
            data-testid="enter-almanac-btn"
          >
            Enter the Almanac
          </motion.button>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7 }}
            onClick={() => setShowAdventure(true)}
            className="pointer-events-auto mt-5 inline-flex items-center gap-1.5 text-white/60 hover:text-white text-xs tracking-[0.15em] uppercase transition-colors"
            data-testid="why-beetle-btn"
          >
            <Sparkles className="w-3.5 h-3.5" /> Why a dung beetle?
          </motion.button>
        </div>

        {/* Scroll hint */}
        <motion.button
          onClick={goToAdventures}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/60 hover:text-white"
          aria-label="Scroll to adventures"
          data-testid="scroll-hint-btn"
        >
          <ChevronDown className="w-7 h-7" />
        </motion.button>
      </section>

      {/* ADVENTURES */}
      <section ref={adventuresRef} className="relative bg-vent-bg bg-pattern py-16 sm:py-24 px-4 sm:px-6" data-testid="adventures-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-14 text-center">
            <p className="text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-3">The Almanac</p>
            <h2 className="font-adventure font-bold text-3xl sm:text-4xl text-white mb-3">Today's Adventures</h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Scouted fresh from the thousands that exist. New ones land as our agent roams —
              <span className="text-zinc-300"> more adventures coming soon.</span>
            </p>
          </div>

          <AdventureGrid />
        </div>
      </section>

      <BeetleAdventureModal open={showAdventure} onClose={() => setShowAdventure(false)} />
    </div>
  );
};

export default AlmanacHome;
